import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { BASE_URL } from "../apiConfig";
import Loading from "./loading";
import { ChevronLeft } from "lucide-react";
import { message } from "antd";
import "./norms.css";

const NormsSetup = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { inputID } = useParams();
  const martID = location.state?.mart_id;
  const marts_name = location.state?.mart_name;

  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [slabValues, setSlabValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isGeneratingMarts, setIsGeneratingMarts] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await fetchMartFields();
      // getMartFieldValues will be called inside fetchMartFields
    };
    loadData();
  }, []);

  const fetchMartFields = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${BASE_URL}/mart_router/get_mart_fields?input_id=${inputID}&mart_id=${martID}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      setFields(data);

      const initialValues = {};
      const initialSlabs = {};

      data.forEach((field) => {
        if (field.field_type === "numeric") {
          initialValues[field.id] = field.default_value || "";
        } else if (field.field_type === "NormRange") {
          initialValues[`${field.id}_threshold1`] = "";
          initialValues[`${field.id}_threshold2`] = "";
        } else if (field.field_type === "NormSlabs") {
          initialSlabs[field.id] = [{ lower: "", upper: "", label: "" }];
        }
      });

      setFieldValues(initialValues);
      setSlabValues(initialSlabs);

      // Now fetch and populate saved values
      await fetchAndPopulateValues(data, initialValues, initialSlabs);

      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const getMartFieldValues = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(
        `${BASE_URL}/mart_router/get_mart_field_values?mart_id=${martID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch mart field values");
      }

      const data = await response.json();

      // Process the response and populate field values
      if (data && Array.isArray(data)) {
        const updatedFieldValues = { ...fieldValues };
        const updatedSlabValues = { ...slabValues };

        data.forEach((item) => {
          const fieldId = item.mart_field_id;
          const fieldValue = item.mart_field_value;

          // Find the field configuration to determine its type
          const field = fields.find((f) => f.id === fieldId);

          if (!field) return;

          if (field.field_type === "numeric") {
            // For numeric fields, directly assign the value
            updatedFieldValues[fieldId] = fieldValue;
          } else if (field.field_type === "NormRange") {
            // For NormRange fields, extract Boundary array
            if (fieldValue?.Boundary && Array.isArray(fieldValue.Boundary)) {
              updatedFieldValues[`${fieldId}_threshold1`] =
                fieldValue.Boundary[0];
              updatedFieldValues[`${fieldId}_threshold2`] =
                fieldValue.Boundary[1];
            }
          } else if (field.field_type === "NormSlabs") {
            // For NormSlabs fields, extract slabs array
            if (fieldValue?.slabs && Array.isArray(fieldValue.slabs)) {
              updatedSlabValues[fieldId] = fieldValue.slabs.map((slab) => ({
                lower: slab.lower.toString(),
                upper: slab.upper.toString(),
                label: slab.label,
              }));
            }
          }
        });

        setFieldValues(updatedFieldValues);
        setSlabValues(updatedSlabValues);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAndPopulateValues = async (
    fieldsData,
    initialValues,
    initialSlabs
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${BASE_URL}/mart_router/get_mart_field_values?mart_id=${martID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) return;

      const data = await response.json();

      if (data && Array.isArray(data)) {
        const updatedFieldValues = { ...initialValues };
        const updatedSlabValues = { ...initialSlabs };

        data.forEach((item) => {
          const fieldId = item.mart_field_id;
          const fieldValue = item.mart_field_value;
          const field = fieldsData.find((f) => f.id === fieldId);

          if (!field) return;

          if (field.field_type === "numeric") {
            updatedFieldValues[fieldId] = fieldValue;
          } else if (field.field_type === "NormRange") {
            if (fieldValue?.Boundary && Array.isArray(fieldValue.Boundary)) {
              updatedFieldValues[`${fieldId}_threshold1`] =
                fieldValue.Boundary[0];
              updatedFieldValues[`${fieldId}_threshold2`] =
                fieldValue.Boundary[1];
            }
          } else if (field.field_type === "NormSlabs") {
            if (fieldValue?.slabs && Array.isArray(fieldValue.slabs)) {
              updatedSlabValues[fieldId] = fieldValue.slabs.map((slab) => ({
                lower: slab.lower.toString(),
                upper: slab.upper.toString(),
                label: slab.label,
              }));
            }
          }
        });

        setFieldValues(updatedFieldValues);
        setSlabValues(updatedSlabValues);
      }
    } catch (err) {
      console.error("Error fetching mart field values:", err);
    }
  };

  const validateFields = () => {
    const newErrors = {};

    fields.forEach((field) => {
      if (field.field_type === "numeric") {
        const value = parseFloat(fieldValues[field.id]);
        if (!fieldValues[field.id]) {
          newErrors[field.id] = "This field is required";
        } else if (isNaN(value)) {
          newErrors[field.id] = "Please enter a valid number";
        }
      } else if (field.field_type === "NormRange") {
        const t1 = parseFloat(fieldValues[`${field.id}_threshold1`]);
        const t2 = parseFloat(fieldValues[`${field.id}_threshold2`]);

        if (!fieldValues[`${field.id}_threshold1`])
          newErrors[`${field.id}_threshold1`] = "This field is required";
        else if (isNaN(t1) || t1 < 0)
          newErrors[`${field.id}_threshold1`] = "Please enter a valid number";

        if (!fieldValues[`${field.id}_threshold2`])
          newErrors[`${field.id}_threshold2`] = "This field is required";
        else if (isNaN(t2) || t2 < 0)
          newErrors[`${field.id}_threshold2`] = "Please enter a valid number";

        if (t1 >= t2 && !isNaN(t1) && !isNaN(t2)) {
          newErrors[`${field.id}_overlap`] =
            "First value must be less than second value";
        }
      } else if (field.field_type === "NormSlabs") {
        const slabs = slabValues[field.id] || [];

        slabs.forEach((slab, idx) => {
          const lower = parseFloat(slab.lower);
          const upper = parseFloat(slab.upper);

          if (!slab.lower)
            newErrors[`${field.id}_slab_${idx}_lower`] =
              "This field is required";
          else if (isNaN(lower) || lower < 0)
            newErrors[`${field.id}_slab_${idx}_lower`] =
              "Please enter a valid number";

          if (!slab.upper)
            newErrors[`${field.id}_slab_${idx}_upper`] =
              "This field is required";
          else if (isNaN(upper) || upper < 0)
            newErrors[`${field.id}_slab_${idx}_upper`] =
              "Please enter a valid number";

          if (!slab.label.trim())
            newErrors[`${field.id}_slab_${idx}_label`] = "Label is required";

          if (!isNaN(lower) && !isNaN(upper) && lower >= upper) {
            newErrors[`${field.id}_slab_${idx}_order`] =
              "Lower value must be less than upper value";
          }

          if (idx > 0) {
            const prevUpper = parseFloat(slabs[idx - 1].upper);
            if (!isNaN(prevUpper) && prevUpper !== lower) {
              newErrors[`${field.id}_slab_${idx}_continuity`] =
                "Lower value must match the previous upper value";
            }
          }
        });

        if (slabs.length === 0) {
          newErrors[`${field.id}_no_slabs`] = "At least one range is required";
        }
      }
    });

    return newErrors;
  };

  const handleFieldChange = (fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    // Clear related errors when field changes
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldId];
      delete newErrors[`${fieldId}_threshold1`];
      delete newErrors[`${fieldId}_threshold2`];
      delete newErrors[`${fieldId}_overlap`];
      return newErrors;
    });
  };

  const addSlab = (fieldId) => {
    setSlabValues((prev) => ({
      ...prev,
      [fieldId]: [
        ...(prev[fieldId] || []),
        { lower: "", upper: "", label: "" },
      ],
    }));
    // Clear slab-related errors when adding new slab
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(fieldId)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const updateSlab = (fieldId, index, key, value) => {
    setSlabValues((prev) => {
      const updated = [...(prev[fieldId] || [])];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [fieldId]: updated };
    });
    // Clear errors for this specific slab field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${fieldId}_slab_${index}_${key}`];
      delete newErrors[`${fieldId}_slab_${index}_order`];
      delete newErrors[`${fieldId}_slab_${index}_continuity`];
      return newErrors;
    });
  };

  const removeSlab = (fieldId, index) => {
    setSlabValues((prev) => {
      const updated = (prev[fieldId] || []).filter((_, i) => i !== index);
      return { ...prev, [fieldId]: updated };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(fieldId)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const saveNorms = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      messageApi.error("Please fix all validation errors");
      // alert("Please fix all validation errors");
      return;
    }
    setErrors({});

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const apiFields = fields.map((field) => {
        let fieldValue;

        if (field.field_type === "numeric") {
          fieldValue = parseFloat(fieldValues[field.id]);
        } else if (field.field_type === "NormRange") {
          const t1 = parseFloat(fieldValues[`${field.id}_threshold1`]);
          const t2 = parseFloat(fieldValues[`${field.id}_threshold2`]);
          fieldValue = { Boundary: [t1, t2] };
        } else if (field.field_type === "NormSlabs") {
          fieldValue = {
            slabs: (slabValues[field.id] || []).map((s) => ({
              lower: parseFloat(s.lower),
              upper: parseFloat(s.upper),
              label: s.label.trim(),
            })),
          };
        }

        return {
          mart_field_id: field.id,
          mart_field_value: fieldValue,
        };
      });

      const payload = { fields: apiFields };
      console.log("Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch(
        `${BASE_URL}/mart_router/add_mart_fields?mart_id=${martID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to save norms");
      }

      const result = await response.json();
      console.log("API Response:", result);

      setIsSaved(true);
      // alert("Norms saved successfully!");
      messageApi.success("Norms saved successfully!");
    } catch (err) {
      console.error(err);
      // alert("Something went wrong");
      messageApi.error("Something went wrong");
    }
  };

  const generateDataMarts = async () => {
    setIsGeneratingMarts(true); // Start loading
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }

      const response = await fetch(
        `${BASE_URL}/mart_router/generate_marts_for_id`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            input_id: inputID,
            mart_id: martID,
            marts_name: marts_name,
          }),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to generate data marts");
      }
      const result = await response.json();
      console.log("Generate Marts Response:", result);

      messageApi.success("Data Marts generation started!");

      navigate(`/architect/${inputID}/marts`, {
        state: {
          input_id: inputID,
          mart_id: martID,
          martsFileName: marts_name,
        },
      });
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to generate data marts");
    } finally {
      setIsGeneratingMarts(false); // Stop loading
    }
  };

  const renderField = (field) => {
    if (field.field_type === "numeric") {
      return (
        <div className="norms-field-card">
          <div className="norms-field-header">
            <h3>{field.field_name}</h3>
            <span className="norms-field-badge">Numeric</span>
          </div>
          <div className="norms-field-body">
            <div className="norms-input-wrapper">
              <input
                type="number"
                value={fieldValues[field.id] || ""}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.default_value || "Enter value"}
                className="norms-numeric-input"
              />
              {errors[field.id] && (
                <span className="norms-error-tooltip">{errors[field.id]}</span>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (field.field_type === "NormRange") {
      return (
        <>
          {contextHolder}
          <div className="norms-field-card">
            <div className="norms-field-header">
              <h3>{field.field_name}</h3>
              <span className="norms-field-badge">Range</span>
            </div>
            <div className="norms-field-body">
              <div className="norms-formula-row">
                <span className="norms-label-text">Low</span>
                <span className="norms-symbol">≤</span>
                <div className="norms-input-wrapper">
                  <input
                    type="number"
                    value={fieldValues[`${field.id}_threshold1`] || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        `${field.id}_threshold1`,
                        e.target.value
                      )
                    }
                    placeholder="0"
                  />
                  {errors[`${field.id}_threshold1`] && (
                    <span className="norms-error-tooltip">
                      {errors[`${field.id}_threshold1`]}
                    </span>
                  )}
                </div>
                <span className="norms-symbol">
                  ≤ <span className="norms-label-text">Medium</span> ≤
                </span>
                <div className="norms-input-wrapper">
                  <input
                    type="number"
                    value={fieldValues[`${field.id}_threshold2`] || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        `${field.id}_threshold2`,
                        e.target.value
                      )
                    }
                    placeholder="0"
                  />
                  {errors[`${field.id}_threshold2`] && (
                    <span className="norms-error-tooltip">
                      {errors[`${field.id}_threshold2`]}
                    </span>
                  )}
                </div>
                <span className="norms-symbol">≤</span>
                <span className="norms-label-text">High</span>
              </div>
              {errors[`${field.id}_overlap`] && (
                <div className="norms-inline-error">
                  {errors[`${field.id}_overlap`]}
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    if (field.field_type === "NormSlabs") {
      const slabs = slabValues[field.id] || [];

      return (
        <div className="norms-field-card">
          <div className="norms-field-header">
            <h3>{field.field_name}</h3>
            <div className="norms-header-right">
              <span className="norms-field-badge">Slabs</span>
              <button
                type="button"
                className="norms-add-slab-header-btn"
                onClick={() => addSlab(field.id)}
              >
                Add Range
              </button>
            </div>
          </div>

          <div className="norms-field-body">
            {slabs.length === 0 ? (
              <div className="norms-empty-slabs">
                No slabs added yet. Use the "+ Add Range" button above to begin.
              </div>
            ) : (
              <div className="norms-slabs-list">
                {slabs.map((slab, idx) => (
                  <div key={idx} className="norms-slab-row">
                    <div className="norms-slab-inputs-horizontal">
                      <div className="norms-slab-input-col">
                        <input
                          type="number"
                          value={slab.lower}
                          onChange={(e) =>
                            updateSlab(field.id, idx, "lower", e.target.value)
                          }
                          placeholder="min %"
                          className="norms-slab-input-field"
                        />
                        {errors[`${field.id}_slab_${idx}_lower`] && (
                          <span className="norms-error-tooltip">
                            {errors[`${field.id}_slab_${idx}_lower`]}
                          </span>
                        )}
                      </div>

                      <div className="norms-slab-input-col">
                        <input
                          type="number"
                          value={slab.upper}
                          onChange={(e) =>
                            updateSlab(field.id, idx, "upper", e.target.value)
                          }
                          placeholder="max %"
                          className="norms-slab-input-field"
                        />
                        {errors[`${field.id}_slab_${idx}_upper`] && (
                          <span className="norms-error-tooltip">
                            {errors[`${field.id}_slab_${idx}_upper`]}
                          </span>
                        )}
                      </div>

                      <div className="norms-slab-input-col norms-label-col">
                        <input
                          type="text"
                          value={slab.label}
                          onChange={(e) =>
                            updateSlab(field.id, idx, "label", e.target.value)
                          }
                          placeholder="Add label"
                          className="norms-slab-input-field"
                        />
                        {errors[`${field.id}_slab_${idx}_label`] && (
                          <span className="norms-error-tooltip">
                            {errors[`${field.id}_slab_${idx}_label`]}
                          </span>
                        )}
                      </div>

                      {slabs.length > 1 && (
                        <button
                          type="button"
                          className="norms-remove-slab-icon"
                          onClick={() => removeSlab(field.id, idx)}
                          title="Remove this slab"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {(errors[`${field.id}_slab_${idx}_order`] ||
                      errors[`${field.id}_slab_${idx}_continuity`]) && (
                      <div className="norms-slab-error-message">
                        {errors[`${field.id}_slab_${idx}_order`] ||
                          errors[`${field.id}_slab_${idx}_continuity`]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {errors[`${field.id}_no_slabs`] && (
              <div className="norms-inline-error">
                {errors[`${field.id}_no_slabs`]}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="norms-container">
        <div className="norms-loading">
          <Loading />
        </div>
      </div>
    );
  }

  const fieldRows = [];
  for (let i = 0; i < fields.length; i += 2) {
    fieldRows.push(fields.slice(i, i + 2));
  }

  return (
    <div className="norms-container">
      <div className="norms-header">
        <ChevronLeft
          style={{ display: "inline-block", cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
        <h1>Norms Setup</h1>
      </div>

      <div className="norms-fields-outer-container">
        <div className="norms-fields-container">
          {fieldRows.map((row, rowIndex) => (
            <div key={rowIndex} className="norms-fields-row">
              {row.map((field) => (
                <div key={field.id} className="norms-field-wrapper">
                  {renderField(field)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="norms-action-bar">
        <button
          className="norms-btn norms-btn-cancel"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        <div className="norms-action-group">
          <button className="norms-btn norms-btn-save" onClick={saveNorms}>
            Save Norms
          </button>
          <button
            className="norms-btn norms-btn-generate"
            onClick={generateDataMarts}
            disabled={!isSaved || isGeneratingMarts}
            style={{
              backgroundColor:
                isSaved && !isGeneratingMarts ? "#c12328" : "#e5e7eb",
              color: isSaved && !isGeneratingMarts ? "white" : "#9ca3af",
              cursor: isSaved && !isGeneratingMarts ? "pointer" : "not-allowed",
              border: "none",
              opacity: isSaved && !isGeneratingMarts ? 1 : 0.6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {isGeneratingMarts && (
              <span
                className="norms-spinner"
                style={{
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #9ca3af",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  animation: "spin 1s linear infinite",
                  display: "inline-block",
                  cursor:"disabled"
                }}
              />
            )}
            {isGeneratingMarts ? "Generating..." : "Generate Marts"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NormsSetup;
